import { useState } from 'react'
import ReactMarkdown from 'react-markdown'
import rehypeSanitize from 'rehype-sanitize'

const MARKDOWN_CLASS =
  'text-harbor-text text-sm leading-relaxed space-y-3 [&_h1]:text-xl [&_h1]:font-semibold ' +
  '[&_h1]:text-harbor-text [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:text-harbor-text ' +
  '[&_h3]:font-medium [&_h3]:text-harbor-text [&_p]:text-harbor-text-secondary ' +
  '[&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_code]:bg-harbor-surface-raised ' +
  '[&_code]:px-1 [&_code]:rounded [&_pre]:bg-harbor-bg [&_pre]:p-3 [&_pre]:rounded-lg ' +
  '[&_pre]:overflow-x-auto [&_a]:text-emerald-400 [&_table]:w-full [&_table]:text-sm ' +
  '[&_th]:text-left [&_th]:font-semibold [&_th]:text-harbor-text [&_td]:border-t ' +
  '[&_td]:border-harbor-border [&_td]:py-1 [&_th]:py-1'

interface PromptMarkdownProps {
  markdown: string
  maxHeightClass?: string
}

/**
 * Rendered-markdown view for prompt bodies with a Rendered/Source toggle.
 * Prompt instructions are Markdown (edited via PromptMarkdownEditor's
 * Write/Preview tabs), so read surfaces must render them — not dump raw
 * text into a <pre>.
 */
export default function PromptMarkdown({
  markdown,
  maxHeightClass = 'max-h-64',
}: PromptMarkdownProps) {
  const [view, setView] = useState<'rendered' | 'source'>('rendered')
  const tabClass = (active: boolean) =>
    `rounded-md px-2 py-0.5 text-xs font-medium transition-colors ${
      active
        ? 'bg-harbor-surface-raised text-harbor-text'
        : 'text-harbor-text-muted hover:text-harbor-text'
    }`

  return (
    <div>
      <div className="mb-2 flex justify-end">
        <div className="flex gap-1 rounded-lg border border-harbor-border bg-harbor-surface p-0.5">
          <button
            type="button"
            className={tabClass(view === 'rendered')}
            onClick={() => setView('rendered')}
          >
            Rendered
          </button>
          <button
            type="button"
            className={tabClass(view === 'source')}
            onClick={() => setView('source')}
          >
            Source
          </button>
        </div>
      </div>
      {view === 'rendered' ? (
        <div
          className={`${maxHeightClass} overflow-auto rounded-lg border border-harbor-border bg-harbor-surface-raised/60 px-4 py-3 ${MARKDOWN_CLASS}`}
        >
          <ReactMarkdown rehypePlugins={[rehypeSanitize]}>{markdown}</ReactMarkdown>
        </div>
      ) : (
        <pre
          className={`${maxHeightClass} overflow-auto whitespace-pre-wrap break-words rounded-lg border border-harbor-border bg-harbor-surface-raised/60 px-3 py-2 font-mono text-xs text-harbor-text-secondary`}
        >
          {markdown}
        </pre>
      )}
    </div>
  )
}
